import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  PhoneCall,
  Filter,
  RefreshCw,
  Users,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Target,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const FollowupPerformanceSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">
      <div className="h-6 w-52 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-24 bg-gray-100 rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="h-[380px] bg-gray-100 rounded-xl" />

        <div className="h-[380px] bg-gray-100 rounded-xl" />
      </div>
    </div>
  );
};

const FollowupPerformanceError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-lg font-semibold text-red-600">
        Failed to load Followup Performance
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const FollowupPerformanceCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getFollowupPerformance.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const bestUser = useMemo(() => {
    if (!data?.performers?.length) return null;

    return [...data.performers].sort(
      (a, b) => b.closedLeads - a.closedLeads,
    )[0];
  }, [data]);

  if (loading) {
    return <FollowupPerformanceSkeleton />;
  }

  if (error) {
    return <FollowupPerformanceError onRetry={fetchReport} />;
  }

  const summary = data.summary;

  const stats = [
    {
      title: "Sales Team",
      value: summary.totalUsers,
      icon: Users,
      bg: "bg-blue-50",
      color: "text-blue-600",
    },
    {
      title: "Overdue Leads",
      value: summary.totalOverdue,
      icon: AlertTriangle,
      bg: "bg-red-50",
      color: "text-red-600",
    },
    {
      title: "Closed Leads",
      value: summary.totalClosed,
      icon: CheckCircle2,
      bg: "bg-green-50",
      color: "text-green-600",
    },
    {
      title: "Total Leads",
      value: summary.totalLeads,
      icon: Target,
      bg: "bg-orange-50",
      color: "text-orange-600",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <PhoneCall size={18} className="text-red-600" />

            <h2 className="font-semibold text-lg">Followup Performance</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {stats.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{item.title}</p>

                      <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </h3>
                    </div>

                    <Icon size={20} className={item.color} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="border rounded-xl p-4 h-[380px]">
              <h3 className="font-semibold mb-4">
                Followup Status Distribution
              </h3>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart}>
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="userName" />

                  <YAxis />

                  <Tooltip />

                  <Legend />

                  <Bar dataKey="overdue" fill="#DC2626" name="Overdue" />

                  <Bar dataKey="closed" fill="#16A34A" name="Closed" />

                  <Bar dataKey="healthy" fill="#2563EB" name="Healthy" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock3 size={18} className="text-orange-600" />

                <h3 className="font-semibold">Team Insight</h3>
              </div>

              {bestUser && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 border rounded-xl p-5">
                  <h4 className="text-xl font-bold">{bestUser.userName}</h4>

                  <p className="text-gray-500 text-sm mt-1">
                    Highest Closed Leads
                  </p>

                  <div className="grid grid-cols-2 gap-4 mt-5">
                    <div>
                      <p className="text-xs text-gray-500">Total Leads</p>

                      <p className="font-bold">{bestUser.totalLeads}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Closed</p>

                      <p className="font-bold text-green-600">
                        {bestUser.closedLeads}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Overdue</p>

                      <p className="font-bold text-red-600">
                        {bestUser.overdueLeads}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500">Healthy</p>

                      <p className="font-bold text-blue-600">
                        {bestUser.healthyLeads}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 overflow-x-auto border rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>

                  <th className="px-4 py-3 text-left">Total Leads</th>

                  <th className="px-4 py-3 text-left">Overdue</th>

                  <th className="px-4 py-3 text-left">Healthy</th>

                  <th className="px-4 py-3 text-left">Closed</th>
                </tr>
              </thead>

              <tbody>
                {data.performers.map((item) => (
                  <tr key={item.userId} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{item.userName}</td>

                    <td className="px-4 py-3">{item.totalLeads}</td>

                    <td className="px-4 py-3 text-red-600">
                      {item.overdueLeads}
                    </td>

                    <td className="px-4 py-3 text-blue-600">
                      {item.healthyLeads}
                    </td>

                    <td className="px-4 py-3 text-green-600">
                      {item.closedLeads}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default FollowupPerformanceCard;
